import { SetStateAction } from "react";
import { CompanyFilter } from "../../../legacy/types/companyFilter";
import { HgProductCategory } from "../../../types/taxonomy";
import { FILTERS_KEYS } from "./constants";

export function clearUploadedAccountsFilterState(
  companyFilter: SetStateAction<CompanyFilter> | undefined
): CompanyFilter {
  return {
    ...companyFilter,
    companyMatch: undefined,
  };
}

export function clearOpportunityTypeFilterState(
  companyFilter: SetStateAction<CompanyFilter> | undefined
): CompanyFilter {
  return {
    ...companyFilter,
    opportunityType: undefined,
  };
}

export function clearTargetOrganizationsFilterState(
  companyFilter: SetStateAction<CompanyFilter> | undefined
): CompanyFilter {
  return {
    ...companyFilter,
    hgIds: undefined,
    hgIdsIncludingCorporateRelatives: undefined,
  };
}

export function clearGeographyFilterState(
  companyFilter: SetStateAction<CompanyFilter> | undefined
): CompanyFilter {
  return {
    ...companyFilter,
    countryAlpha2s: undefined,
    excludedCountryAlpha2s: undefined,
    regionNames: undefined,
    stateIds: undefined,
  };
}

export function clearRangeFilterState(
  rangeFilterKey: string,
  rangeFixedFilterKey: string,
  companyFilter: SetStateAction<CompanyFilter> | undefined
): CompanyFilter {
  return {
    ...companyFilter,
    [rangeFilterKey]: undefined,
    [rangeFixedFilterKey]: undefined,
  };
}
export const getAllPossibleDescendantIds = (
  rootCategoryId: string,
  childrenByParentId: Map<string, string[]>,
  categoriesById: Map<string, HgProductCategory>
): Set<string> => {
  const descendantIds = new Set<string>();
  if (!categoriesById.has(rootCategoryId)) {
    return descendantIds;
  }

  const queue: string[] = [rootCategoryId];
  const visited = new Set<string>();

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    if (visited.has(currentId)) {
      continue;
    }
    visited.add(currentId);
    descendantIds.add(currentId);

    const children = childrenByParentId.get(currentId) || [];
    for (const childId of children) {
      if (categoriesById.has(childId) && !visited.has(childId)) {
        queue.push(childId);
      }
    }
  }
  return descendantIds;
};

/**
 * Utility function to check if a filter has values based on its filterKey
 */
export const isFilterFilled = (
  filterKey: string,
  companyFilter: CompanyFilter | undefined
): boolean => {
  if (!companyFilter) return false;

  switch (filterKey) {
    case FILTERS_KEYS.opportunityType:
      return !!companyFilter?.opportunityType?.type;

    case FILTERS_KEYS.targetOrganizations:
      return !!(
        companyFilter?.hgIds?.length ||
        companyFilter?.hgIdsIncludingCorporateRelatives?.length
      );

    case FILTERS_KEYS.geography:
      return [
        companyFilter.countryAlpha2s?.length,
        companyFilter.excludedCountryAlpha2s?.length,
        companyFilter.regionNames?.length,
        companyFilter.stateIds?.length,
      ].some(Boolean);

    case FILTERS_KEYS.globalPresence:
      return [
        companyFilter?.globalPresence?.isMultiNational,
        companyFilter?.globalPresence?.hqCountryCodes?.length,
        companyFilter?.globalPresence?.hqRegions?.length,
        companyFilter?.globalPresence?.subsidiaryLocations?.countryCodes
          ?.length,
        companyFilter?.globalPresence?.subsidiaryLocations?.regions?.length,
      ].some(Boolean);

    case FILTERS_KEYS.companyType:
      return !!(
        companyFilter.isDp ||
        companyFilter.isGhq ||
        companyFilter.isGp
      );

    case FILTERS_KEYS.industry:
      return [
        companyFilter?.industry?.industryIds?.length,
        companyFilter?.industry?.excludedIndustryIds?.length,
        companyFilter?.industry?.naics2012Ids?.length,
        companyFilter?.industry?.excludedNaics2012Ids?.length,
        companyFilter?.industry?.sicUS1987Ids?.length,
        companyFilter?.industry?.excludedSicUS1987Ids?.length,
      ].some(Boolean);

    case FILTERS_KEYS.revenue:
      return [
        companyFilter?.revenue?.min !== undefined,
        companyFilter?.revenue?.max !== undefined,
      ].some(Boolean);

    case FILTERS_KEYS.employees:
      return [
        companyFilter?.employees?.min !== undefined,
        companyFilter?.employees?.max !== undefined,
      ].some(Boolean);

    case FILTERS_KEYS.productCategories:
      return !!companyFilter?.productCategories?.ids?.length;

    case FILTERS_KEYS.products:
      return !!companyFilter?.products?.some((p) => p.ids?.length);

    case FILTERS_KEYS.vendors:
      return !!companyFilter?.vendors?.some((v) => v.ids?.length);

    case FILTERS_KEYS.productAttributes:
      return !!companyFilter?.productAttributes?.ids?.length;

    default:
      return false;
  }
};

export const clearFiltersByKeys = (
  keysToDelete: string[],
  filters: CompanyFilter
): CompanyFilter => {
  let filtersToDelete = { ...filters };

  keysToDelete.forEach((key) => {
    if (key === FILTERS_KEYS.targetOrganizations) {
      filtersToDelete = clearTargetOrganizationsFilterState(filtersToDelete);
    } else if (key === FILTERS_KEYS.geography) {
      filtersToDelete = clearGeographyFilterState(filtersToDelete);
    } else if ([FILTERS_KEYS.employees, FILTERS_KEYS.revenue].includes(key)) {
      const fixed =
        key === FILTERS_KEYS.employees
          ? "hasFixedEmployees"
          : "hasFixedRevenue";
      filtersToDelete = clearRangeFilterState(key, fixed, filtersToDelete);
    } else {
      filtersToDelete = { ...filtersToDelete, [key]: undefined };
    }
  });

  return filtersToDelete;
};
